import * as React from 'react';
import { Autocomplete, Box, TextField, FormControl } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { SuccessFailureIndicator } from '../../../subs/SuccessFailureIndicator';
import { copyArrayWithIndexChanged } from '../../../Utils';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { WorkshopProductCombination, getWSProductCombinations, updateProdTestCombination } from '../../../types/WorkshopProductCombination';
import { Workshop, getWorkshops } from '../../../types/Workshop';
import { EditableTable } from '../../../subs/EditableTable';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../../contexts/SnackBarContext';

export const WorkshopProductCombinationAdminView = () => {
    const { showSnackbar } = useSnackbar();
    const [workshops, setWorkshops] = React.useState<Workshop[]>([]);
    const [workshop, setWorkshop] = React.useState<Workshop>();
    const [allData, setallData] = React.useState<WorkshopProductCombination[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchAllWorkshops = async () => {
            const allworkshopsResponse = await getWorkshops(new AbortController());
            if (allworkshopsResponse.failed) {
                showUserFeedback(allworkshopsResponse.errorText);
                return;
            }
            setWorkshops(allworkshopsResponse.result);
        }
        fetchAllWorkshops();
    }, [showUserFeedback]);

    React.useEffect(() => {
        const fetchAllWorkshopProductCombinations = async () => {
            if (workshop === undefined || workshop.id === 0) return;
            const allCompaniesResponse = await getWSProductCombinations(workshop.id, new AbortController());
            if (allCompaniesResponse.failed) {
                showUserFeedback(allCompaniesResponse.errorText);
                return;
            }
            setallData(allCompaniesResponse.result);
        }
        fetchAllWorkshopProductCombinations();
    }, [workshop, showUserFeedback]);

    const processRowUpdate = (newRow: GridRowModel) => {
        const changedData = allData.find(row => row.id === newRow.id);
        if (!changedData) {
            showUserFeedback('Row not found', 'error');
            return newRow;
        }
        changedData.consumerReadyToTest = newRow.consumerReadyToTest as boolean;
        changedData.isConsumer = newRow.consumerReadyToTest as boolean;
        changedData.isProvider = newRow.consumerReadyToTest as boolean;
        changedData.providerReadyToTest = newRow.consumerReadyToTest as boolean;
        saveHandler(changedData);
        return newRow;
    };

    const saveHandler = async (changedData: WorkshopProductCombination) => {
        const updateWSPCRequest = await updateProdTestCombination(changedData, new AbortController());

        if (updateWSPCRequest.failed) {
            showUserFeedback(updateWSPCRequest.errorText);
            return;
        }
        const updateWSPC = updateWSPCRequest.result;
        let allWSPCLoc = copyArrayWithIndexChanged(allData, allData.findIndex(tc => tc.id === updateWSPC.id), updateWSPC);
        setallData(allWSPCLoc);
    }

    const columns: GridColDef[] = [
        {
            field: 'guid',
            headerName: 'GUID',
            flex: 2
        },
        {
            field: 'testCombinationId',
            headerName: 'Test Combination ID',
            flex: 1,
        },
        {
            field: 'workshopProductId',
            headerName: 'Workshop Product ID',
            flex: 1,
        },
        {
            field: 'isConsumer',
            headerName: 'Is Consumer',
            type: 'boolean',
            flex: 1,
            editable: true,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.isConsumer} /> },
        },
        {
            field: 'isProvider',
            headerName: 'Is Provider',
            type: 'boolean',
            flex: 1,
            editable: true,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.isProvider} /> },
        },
        {
            field: 'consumerReadyToTest',
            headerName: 'Consumer Ready To Test',
            type: 'boolean',
            flex: 1,
            editable: true,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.consumerReadyToTest} /> },
        },
        {
            field: 'providerReadyToTest',
            headerName: 'Provider Ready To Test',
            type: 'boolean',
            flex: 1,
            editable: true,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.providerReadyToTest} /> },
        },
        {
            field: 'id'
        },
    ];

    return <React.Fragment >
        <Grid size={6} paddingBottom={10}>
            <FormControl required fullWidth>
                <Autocomplete id='ac-wsOptions' options={workshops.map(w => w.name)}
                    value={workshop ? workshop.name : ''}
                    onChange={(e, value) => setWorkshop(workshops.find(w => w.name === value))}
                    renderInput={(params) =>
                        <TextField name='wsOptions' multiline
                            {...params}
                            label={"Workshop"}
                            sx={{
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: 'divider',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                    borderColor: 'divider',
                                },
                            }}
                        />
                    }
                />
            </FormControl>
        </Grid>
        {
            workshop &&
            <Box sx={{ flexGrow: 1, width: "100%" }} style={{ overflow: 'auto' }}>
                <EditableTable
                    columns={columns}
                    rows={allData}
                    setAllRows={setallData}
                    processRowUpdate={processRowUpdate}
                />
            </Box>
        }
    </React.Fragment >
}